var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import ArrayHelper from "../helper/ArrayHelper.js";
import DomDataStore from "../data/DomDataStore.js";
import Widget from "./Widget.js";
const isActive = (mask) => mask.element.classList.contains("b-visible"), isPending = (mask) => !isActive(mask) && mask.isVisible;
const _Mask = class _Mask extends Widget {
  /**
   * Returns the array of `Mask` instances for the given `element`.
   * @param {HTMLElement} element The element for which to return `Mask` instances
   * @returns {Core.widget.Mask|Core.widget.Mask[]}
   */
  static get(element) {
    return DomDataStore.get(element, "masks") || DomDataStore.set(element, "masks", []);
  }
  static getActive(element) {
    return _Mask.get(element).find(isActive) || null;
  }
  static getPending(element) {
    return _Mask.get(element).filter(isPending).sort(Widget.weightSortFn);
  }
  /**
   * Shows a mask with the specified message.
   *
   * Masks stack, call {@link #function-unmask-static} to remove the topmost mask. Or call {@link #function-close}
   * on the returned mask to close it specifically. Only one mask is displayed for a given `target` element at a time.
   * For example, if two masks are added to an element, the first mask is displayed. If the first mask is closed,
   * then the second mask will become visible.
   *
   * @param {String|MaskConfig} text Message
   * @param {HTMLElement} [target] The element to mask (default is `document.body`)
   * @returns {Core.widget.Mask}
   */
  static mask(text, target = document.body) {
    return _Mask.new({ target }, text);
  }
  static mergeConfigs(...sources) {
    sources = sources.map((c) => typeof c === "string" ? { text: c } : c);
    return super.mergeConfigs(...sources);
  }
  static sync(target) {
    const active = _Mask.getActive(target), pending = !active && _Mask.getPending(target)[0] || null, mask = active || pending, mode = (mask == null ? void 0 : mask.mode) || "", cls = mode.endsWith("blur") ? `b-masked-${mode}` : "";
    let child, classList, remove;
    if (pending) {
      classList = pending.element.classList;
      if (pending.showDelay && !target.classList.contains("b-masked")) {
        classList.add("b-delayed-show");
        pending.delayedShow();
      }
      classList.add("b-visible");
    }
    target.classList.toggle("b-masked", Boolean(mask));
    for (child of target.children) {
      classList = child.classList;
      if (!classList.contains("b-mask")) {
        remove = Array.from(classList).filter((c) => c.startsWith("b-masked-") && cls !== c);
        remove.length && classList.remove(...remove);
        cls && classList.add(cls);
      }
    }
  }
  /**
   * Close the most recently created mask for the specified element.
   * @param {HTMLElement} [element] The element to unmask (default is `document.body`)
   * @returns {Promise|null} A promise which is resolved when the mask is gone, or null if element is not masked
   */
  static unmask(element = document.body) {
    var _a;
    const masks = _Mask.get(element), n = masks.length;
    return n ? (_a = masks[n - 1]) == null ? void 0 : _a.close() : null;
  }
  /**
   * Close all masks for the specified element
   * @param {HTMLElement} [element] The element to unmask (default is `document.body`)
   * @returns {Promise|null} A promise which is resolved when the mask is gone, or null if element is not masked
   * @internal
   */
  static unmaskAll(element = document.body) {
    _Mask.get(element).filter((mask) => !isActive(mask)).forEach((m) => m.destroy());
    return _Mask.unmask(element);
  }
  //endregion
  //region Init
  destroy() {
    if (this.type !== "trial") {
      super.destroy();
    }
  }
  compose() {
    const { icon, maxProgress, mode, progress, showDelay, text, useTransition } = this;
    return {
      class: {
        "b-mask": 1,
        "b-delayed-show": showDelay,
        "b-progress": maxProgress,
        "b-prevent-transitions": !useTransition,
        // 'b-hidden'              : hidden,
        // 'b-visible'             : !hidden,
        [`b-mask-${mode}`]: 1
      },
      children: {
        maskContent: {
          class: "b-mask-content",
          children: {
            progressElement: maxProgress ? {
              class: "b-mask-progress-bar",
              style: {
                width: `${Math.max(0, Math.min(100, Math.round(progress / maxProgress * 100)))}%`
              }
            } : null,
            maskText: {
              class: "b-mask-text",
              html: (icon ? `<i class="b-mask-icon ${icon}"></i>` : "") + text
            }
          }
        }
      }
    };
  }
  //endregion
  //region Config
  generateAutoId() {
    const { type } = this;
    return `mask${typeof type === "string" ? type.trim() : ""}-${_Mask.counter++}`;
  }
  updateAutoClose(delay) {
    this.deferredClose.cancel();
    if (delay) {
      this.deferredClose.delay = delay;
      this.deferredClose();
    }
  }
  updateCover() {
    this.syncCover();
  }
  syncCover() {
    var _a, _b;
    (_b = (_a = this.owner) == null ? void 0 : _a.syncMaskCover) == null ? void 0 : _b.call(_a, this);
  }
  set error(value) {
    this.setConfig({
      ...this.errorDefaults,
      text: value
    });
  }
  onOwnerResize() {
    this.syncCover();
  }
  updateOwner(owner) {
    this.detachListeners("cover");
    owner == null ? void 0 : owner.ion({
      name: "cover",
      recompose: "onOwnerResize",
      resize: "onOwnerResize",
      thisObj: this
    });
  }
  updateShowDelay(delay) {
    const { delayedShow } = this;
    delayedShow.delay = delay;
    if (!delay) {
      delayedShow.flush();
    }
  }
  changeTarget(target) {
    if (target === void 0) {
      target = document.body;
    } else if (typeof target === "string") {
      target = this.owner[target];
    }
    return target;
  }
  updateTarget(target, was) {
    const me = this, { id, element } = me, masks = was && _Mask.get(was);
    if (was) {
      if (was[id] === me) {
        delete was[id];
      }
      ArrayHelper.remove(masks, me);
      _Mask.sync(was);
    }
    if (target) {
      if (!target[id]) {
        target[id] = me;
      }
      if (element.parentNode !== target) {
        target.appendChild(element);
      }
      ArrayHelper.include(_Mask.get(target), me);
      _Mask.sync(target);
    } else {
      element.remove();
    }
  }
  //endregion
  //region Show & hide
  deferredClose() {
    var _a;
    const me = this, { owner } = me;
    me.close().then(() => {
      var _a2;
      return (_a2 = owner == null ? void 0 : owner.onMaskAutoClose) == null ? void 0 : _a2.call(owner, me);
    });
    (_a = owner == null ? void 0 : owner.onMaskAutoClosing) == null ? void 0 : _a.call(owner, me);
  }
  delayedShow() {
    this.element.classList.remove("b-delayed-show");
  }
  updateHidden(hidden, was) {
    var _a;
    (_a = super.updateHidden) == null ? void 0 : _a.call(this, hidden, was);
    hidden && this.element.classList.remove("b-visible");
    _Mask.sync(this.target);
  }
  afterShow(...args) {
    var _a;
    (_a = super.afterShow) == null ? void 0 : _a.call(this, ...args);
    _Mask.sync(this.target);
  }
  afterHide(...args) {
    var _a;
    (_a = super.afterHide) == null ? void 0 : _a.call(this, ...args);
    this.element.classList.remove("b-visible");
    _Mask.sync(this.target);
  }
  /**
   * Close mask (removes it)
   * @returns {Promise} A promise which is resolved after the mask is closed and destroyed.
   */
  async close() {
    await this.hide();
    this.destroy();
  }
  //endregion
};
//region Config
__publicField(_Mask, "$name", "Mask");
__publicField(_Mask, "type", "mask");
__publicField(_Mask, "configurable", {
  /**
   * Set this config to trigger an automatic close after the desired delay:
   * ```javascript
   *  mask.autoClose = 2000;
   * ```
   * If the mask has an `owner`, its `onMaskAutoClosing` method is called when the close starts and its
   * `onMaskAutoClose` method is called when the close finishes.
   * @config {Number}
   * @private
   */
  autoClose: null,
  /**
   * The portion of the {@link #config-target} element to be covered by this mask. By default, the mask fully
   * covers the `target`. In some cases, however, it may be desired to only cover the `'body'` (for example,
   * in a grid).
   *
   * This config is set in conjunction with `owner` which implements the method `syncMaskCover`.
   *
   * @config {String}
   * @private
   */
  cover: null,
  /**
   * The icon to show next to the text. Defaults to showing a spinner
   * @config {String}
   * @default
   */
  icon: "b-icon b-icon-spinner",
  errorDefaults: {
    icon: "b-icon b-icon-warning",
    autoClose: 3e3,
    showDelay: 0
  },
  /**
   * The maximum value of the progress indicator
   * @property {Number}
   */
  maxProgress: null,
  /**
   * Mode: bright, bright-blur, dark or dark-blur
   * @config {'bright'|'bright-blur'|'dark'|'dark-blur'}
   * @default
   */
  mode: "dark",
  /**
   * Number expressing the progress
   * @property {Number}
   */
  progress: null,
  // The owner is involved in the following features:
  //
  // - The `autoClose` timer calls `onMaskAutoClose`.
  // - The `cover` config calls `syncMaskCover`.
  // - If the `target` is a string, that string names the property of the `owner` that holds the
  //   `HTMLElement` reference.
  /**
   * The element to be masked. If this config is a string, that string is the name of the property of the
   * {@link #config-owner} that holds the `HTMLElement` that is the actual target of the mask.
   *
   * NOTE: In prior releases, this used to be specified as the `element` config.
   *
   * @config {String|HTMLElement}
   */
  target: {
    $config: "nullify",
    value: void 0
  },
  /**
   * The text (or HTML) to show in mask
   * @prp {String}
   */
  text: null,
  type: null,
  /**
   * The number of milliseconds to delay before making the mask visible. If set, the mask will have an
   * initial `opacity` of 0 but will function in all other ways as a normal mask. Setting this delay can
   * reduce flicker in cases where load operations are typically short (for example, a second or less).
   *
   * @config {Number}
   */
  showDelay: null,
  useTransition: false
});
__publicField(_Mask, "delayable", {
  deferredClose: 0,
  delayedShow: 0,
  syncCover: {
    type: "throttle",
    delay: 100
  }
});
//endregion
//region Static
// Used to give masks unique names
__publicField(_Mask, "counter", 0);
let Mask = _Mask;
Mask.initClass();
Mask._$name = "Mask";
export {
  Mask as default
};
